'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import type { DemoLoginAccount, DemoCredentialSource } from '@/lib/auth/demoCredentials'

/**
 * Renders test credentials only after client mount to avoid SSR hydration
 * mismatch (React #418) — and only what the SERVER says may be shown.
 *
 * Owner rule 2026-09-15: displayed login credentials show ONLY when enabled
 * AND properly configured. `/api/auth/demo-credentials` decides both (the
 * deployment switch + platform setting; every offered identity existing here,
 * under this environment's address, confirmed, not banned) and returns the
 * accounts it could prove. This component never derives an address itself,
 * never reads an account list from runtime config, has no host-name bypass
 * and no NODE_ENV bypass: before this it showed
 * `officer@production.neutralaiz.com` on prod while auth held
 * `officer@test.neutralaiz.com`, and the click "did nothing".
 *
 * `requireExplicitCredentials` (the public homepage panel) accepts only the
 * explicit numbered accounts (`source: 'configured'`, the CPU compose opt-in);
 * the login page accepts whichever list the route proved.
 *
 * ⛔ DO NOT RE-ADD "Sean O'Brien|sean.obrien@tps.ca|password123" anywhere on
 * this path. Both accounts exist and are confirmed; the password hashes differ
 * (`ui-frontend/supabase/migrations/20260820_officer_account_email_swap.sql`
 * swapped two emails and never moved the passwords). The route's identity
 * check is what now makes such a button impossible to advertise by accident.
 */
type RouteBody = { accounts?: DemoLoginAccount[]; source?: DemoCredentialSource }

export function TestCredentials({ onSelect, className = '', requireExplicitCredentials = false }: { onSelect?: (email: string, password: string) => void; className?: string; requireExplicitCredentials?: boolean }) {
    const [show, setShow] = useState(false)
    const [accounts, setAccounts] = useState<DemoLoginAccount[]>([])

    useEffect(() => {
        setShow(false)
        setAccounts([])
        let cancelled = false
        ;(async () => {
            try {
                const res = await fetch('/api/auth/demo-credentials', { cache: 'no-store', credentials: 'same-origin' })
                if (!res.ok) return
                const body = (await res.json()) as RouteBody
                const list = Array.isArray(body.accounts) ? body.accounts : []
                if (cancelled || list.length === 0) return
                if (requireExplicitCredentials && body.source !== 'configured') return
                setAccounts(list)
                setShow(true)
            } catch {
                // Unreachable route = nothing proven = nothing shown.
            }
        })()
        return () => { cancelled = true }
    }, [requireExplicitCredentials])

    if (!show) return null

    return (
        <div data-demo-id="login-test-creds" className={`w-full p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg ${className}`}>
            <p className="text-xs font-medium text-amber-400 mb-2">&#x1F9EA; Test Credentials</p>
            <div className="space-y-1 text-xs text-amber-300/80">
                {accounts.map(({ label, email, password }) => {
                    const content = <><strong>{label}:</strong> <span className="break-all">{email} / {password}</span></>
                    return onSelect ? (
                        <button
                            key={email}
                            type="button"
                            onClick={() => onSelect(email, password)}
                            className="block w-full text-left font-medium hover:bg-amber-500/15 rounded px-2 py-1 transition-colors cursor-pointer"
                            title={`Sign in as ${label}`}
                        >{content}</button>
                    ) : (
                        <div key={email} className="px-2 py-1 font-medium">
                            <p>{content}</p>
                            <Link href="/login" className="mt-2 inline-block underline">Sign in as {label}</Link>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}
